<template>
  <a-modal
    :visible="visible"
    :title="
      $t('tresorerie.modifierLe') +
      ' ' +
      (chequeData.type === 'CHEQUE'
        ? $t('tresorerie.cheque')
        : $t('tresorerie.traite'))
    "
    :width="600"
    :footer="null"
    @cancel="handleCancel"
    :confirmLoading="loading"
  >
    <a-form-model
      ref="editForm"
      :model="form"
      :rules="rules"
      :label-col="{ span: 8 }"
      :wrapper-col="{ span: 16 }"
    >
      <!-- Numéro -->
      <a-form-model-item :label="$t('tresorerie.numero')" prop="num">
        <a-input
          v-model="form.num"
          :placeholder="$t('tresorerie.numeroChequeTraite')"
          :disabled="loading"
        />
      </a-form-model-item>

      <!-- Banque -->
      <a-form-model-item :label="$t('tresorerie.banque')" prop="bank">
        <bank-select v-model="form.bank" :disabled="loading" />
      </a-form-model-item>

      <!-- Titulaire (only for CHEQUE type) -->
      <a-form-model-item
        v-if="chequeData.type === 'CHEQUE'"
        :label="$t('tresorerie.titulaire')"
        prop="titulaire"
      >
        <a-input
          v-model="form.titulaire"
          :placeholder="$t('tresorerie.nomTitulaire')"
          :disabled="loading"
        />
      </a-form-model-item>

      <!-- Date d'échéance -->
      <a-form-model-item
        :label="$t('tresorerie.dateEcheance')"
        prop="dateEchance"
      >
        <a-date-picker
          v-model="form.dateEchance"
          :placeholder="$t('tresorerie.dateEcheance')"
          format="DD/MM/YYYY"
          style="width: 100%"
          :disabled="loading"
        />
      </a-form-model-item>

      <!-- Note -->
      <a-form-model-item :label="$t('tresorerie.note')" prop="note">
        <a-textarea
          v-model="form.note"
          :placeholder="$t('tresorerie.noteOptionnelle')"
          :rows="3"
          :disabled="loading"
        />
      </a-form-model-item>

      <!-- Read-only fields for information -->
      <a-divider>{{ $t("tresorerie.informationsNonModifiables") }}</a-divider>

      <!-- Montant (Read-only) -->
      <a-form-model-item :label="$t('tresorerie.montant')">
        <a-input
          :value="formatAmount(chequeData.montant)"
          disabled
          style="background-color: #f5f5f5"
        />
      </a-form-model-item>

      <!-- Type (Read-only) -->
      <a-form-model-item :label="$t('tresorerie.type')">
        <a-input
          :value="getTypeLabel(chequeData.type)"
          disabled
          style="background-color: #f5f5f5"
        />
      </a-form-model-item>

      <!-- Statut Banque (Read-only) -->
      <a-form-model-item :label="$t('tresorerie.statutBanque')">
        <a-tag
          :color="getChequeStatutColor(chequeData.bankStatus)"
          style="font-size: 14px; padding: 4px 8px"
        >
          {{ $t("status." + chequeData.bankStatus) }}
        </a-tag>
      </a-form-model-item>
    </a-form-model>

    <!-- Action Buttons -->
    <div style="text-align: right; margin-top: 24px">
      <a-button @click="handleCancel" :disabled="loading">
        {{ $t("all.cancel") }}
      </a-button>
      <a-button
        type="primary"
        @click="handleSubmit"
        :loading="loading"
        style="margin-left: 8px"
      >
        {{
          loading ? $t("tresorerie.modification") : $t("tresorerie.modifier")
        }}
      </a-button>
    </div>
  </a-modal>
</template>

<script>
import apiClient from "@/services/axios";
import moment from "moment";
import BankSelect from "@/components/BankSelect.vue";
import { getChequeStatutColor } from "@/utilities/settings";
import { formatCurrency } from "@/utilities/countrySpecific";

export default {
  name: "EditChequeModal",
  components: {
    BankSelect,
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    chequeData: {
      type: Object,
      default: () => ({}),
    },
  },
  data() {
    return {
      loading: false,
      form: {
        num: "",
        bank: "",
        titulaire: "",
        dateEchance: null,
        note: "",
      },
    };
  },
  computed: {
    rules() {
      const baseRules = {
        num: [
          {
            required: true,
            message: this.$t("tresorerie.numeroRequis"),
            trigger: "blur",
          },
        ],
        bank: [
          {
            required: true,
            message: this.$t("tresorerie.banqueRequis"),
            trigger: "blur",
          },
        ],
        dateEchance: [
          {
            required: true,
            message: this.$t("tresorerie.dateEcheanceRequis"),
            trigger: "change",
          },
        ],
      };

      // Only add titulaire validation for CHEQUE type
      if (this.chequeData.type === "CHEQUE") {
        baseRules.titulaire = [
          {
            required: true,
            message: this.$t("tresorerie.titulaireRequis"),
            trigger: "blur",
          },
        ];
      }

      return baseRules;
    },
  },
  watch: {
    visible(newVal) {
      if (newVal && this.chequeData) {
        this.initializeForm();
      }
    },
    chequeData: {
      handler(newVal) {
        if (newVal && this.visible) {
          this.initializeForm();
        }
      },
      deep: true,
    },
  },
  methods: {
    getChequeStatutColor,
    initializeForm() {
      if (this.chequeData) {
        this.form = {
          num: this.chequeData.num || "",
          bank: this.chequeData.bank || "",
          titulaire:
            this.chequeData.type === "CHEQUE"
              ? this.chequeData.titulaire || ""
              : "",
          dateEchance: this.chequeData.dateEchance
            ? moment(this.chequeData.dateEchance)
            : null,
          note: this.chequeData.note || "",
        };
      }
    },

    formatAmount(amount) {
      return formatCurrency(amount || 0);
    },

    getTypeLabel(type) {
      const types = {
        CHEQUE: this.$t("tresorerie.cheque"),
        TRAITE: this.$t("tresorerie.traite"),
        VIREMENT: this.$t("tresorerie.virement"),
      };
      return types[type] || type;
    },

    getStatusLabel(status) {
      const statuses = {
        active: this.$t("tresorerie.actif"),
        inactive: this.$t("tresorerie.inactif"),
      };
      return statuses[status] || status;
    },

    async handleSubmit() {
      try {
        // Validate form
        const valid = await this.$refs.editForm.validate();
        if (!valid) {
          return;
        }

        this.loading = true;

        // Prepare data for API - only send editable fields
        const updateData = {
          num: this.form.num,
          bank: this.form.bank,
          dateEchance: this.form.dateEchance
            ? this.form.dateEchance.toISOString()
            : null,
          note: this.form.note,
        };

        // Only add titulaire for CHEQUE type
        if (this.chequeData.type === "CHEQUE") {
          updateData.titulaire = this.form.titulaire;
        }

        // Call API to update cheque
        const response = await apiClient.patch(
          `/compV3/cheque-traite/${this.chequeData._id}`,
          updateData
        );

        if (response.data.success) {
          this.$message.success(this.$t("tresorerie.chequeModifieSucces"));
          this.$emit("updated", response.data.data);
          this.handleCancel();
        } else {
          this.$message.error(this.$t("tresorerie.erreurModification"));
        }
      } catch (error) {
        console.error("Error updating cheque:", error);
        this.$message.error(this.$t("tresorerie.erreurModification"));
      } finally {
        this.loading = false;
      }
    },

    handleCancel() {
      this.$refs.editForm.resetFields();
      this.$emit("close");
    },
  },
};
</script>

<style scoped>
.ant-form-item {
  margin-bottom: 16px;
}

.ant-modal-body {
  padding: 24px;
}

.ant-divider {
  margin: 16px 0;
  font-weight: 500;
  color: #666;
}
</style>
